import React, { useState, useRef } from 'react';
import { IconButton, Stack, Typography } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

interface AudioRecorderProps {
  onRecordingComplete: (audioBlob: Blob) => void;
}

const AudioRecorder: React.FC<AudioRecorderProps> = ({ onRecordingComplete }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setRecordedAudio(audioBlob);
        onRecordingComplete(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('无法访问麦克风，请确保已授予麦克风访问权限。');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playRecording = async () => {
    try {
      if (!recordedAudio) return;

      // 如果已经在播放，先停止
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current.currentTime = 0;
      }

      // 创建新的 Audio 对象
      const audioUrl = URL.createObjectURL(recordedAudio);
      const audio = new Audio(audioUrl);
      audioPlayerRef.current = audio;

      // 设置事件处理
      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => {
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl); // 清理 URL
      };
      audio.onerror = (e) => {
        console.error('Error playing audio:', e);
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
      };

      // 播放音频
      await audio.play();
    } catch (error) {
      console.error('Error playing recording:', error);
      setIsPlaying(false);
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {!isRecording ? (
        <IconButton
          onClick={startRecording}
          size="medium"
          sx={{
            border: '2px solid #2c1810',
            color: '#2c1810',
            p: 1,
            '&:hover': {
              backgroundColor: '#2c1810',
              color: '#faf7f2',
            }
          }}
        >
          <MicIcon />
        </IconButton>
      ) : (
        <IconButton
          onClick={stopRecording}
          size="medium"
          sx={{
            border: '2px solid #2c1810',
            color: '#2c1810',
            p: 1,
            '&:hover': {
              backgroundColor: '#2c1810',
              color: '#faf7f2',
            }
          }}
        >
          <StopIcon />
        </IconButton>
      )}
      {recordedAudio && !isRecording && (
        <IconButton
          onClick={playRecording}
          size="medium"
          disabled={isPlaying}
          sx={{
            border: '2px solid #2c1810',
            color: '#2c1810',
            p: 1,
            '&:hover': {
              backgroundColor: '#2c1810',
              color: '#faf7f2',
            },
            '&.Mui-disabled': {
              border: '2px solid rgba(44, 24, 16, 0.3)',
              color: 'rgba(44, 24, 16, 0.3)',
            }
          }}
        >
          <PlayArrowIcon />
        </IconButton>
      )}
      {isRecording && (
        <Typography 
          variant="caption" 
          sx={{ 
            color: '#2c1810',
            fontFamily: '"Playfair Display", serif',
            fontStyle: 'italic',
          }}
        >
          录音中...
        </Typography>
      )}
    </Stack>
  );
};

export default AudioRecorder; 